import React from 'react';
import ReactDOM from 'react-dom/client';
import  App from './App.jsx';
import { isMaskedHost, resolveMaskedDomain, maskedTemplate, provisionStores, OFFICE_ORIGIN } from "./util/maskedDomain";
import { prefetchTemplate } from "./pages/marketplace/templateChunks.js";
import { attemptRecoveryReload } from "./util/chunkRecovery";
import { startVersionWatchdog } from "./util/versionWatchdog";
import { AppErrorBoundary } from "./components/appStatus.jsx";

// Boot marker for the inline last-resort recovery script in index.html: if
// this module never executes (stale HTML whose entry chunk 404s), the inline
// script sees the marker missing and performs a cache-busting reload.
window.__AREGO_BOOT_OK = true;

// Cosmetic: drop a leftover recovery cache-buster (?cb=...) before the
// routers read the URL.
if (/[?&]cb=/.test(window.location.search)) {
  try {
    const u = new URL(window.location.href);
    u.searchParams.delete("cb");
    window.history.replaceState(null, "", u.pathname + u.search + u.hash);
  } catch { /* leave the param */ }
}

// Some production/CDN edge-cases can trigger "Unable to preload CSS for ..." which may
// bubble up as an unhandled error. Filter this specific message so it doesn't break the app UI.
if (typeof window !== "undefined") {
  const isPreloadCssError = (msg) =>
    typeof msg === "string" && msg.toLowerCase().includes("unable to preload css for");

  window.addEventListener("error", (event) => {
    try {
      const msg = event?.message ?? event?.error?.message ?? "";
      if (isPreloadCssError(msg)) {
        // Prevent the error from escalating to the app error boundary.
        event.preventDefault?.();
        return;
      }
    } catch {
      // Ignore filter failures.
    }
  });

  // After a redeploy the previous build's hashed chunks are gone, so a stale
  // session hits "Failed to fetch dynamically imported module: …/assets/x-HASH.js".
  // Vite fires vite:preloadError for these. When the shared recovery budget
  // (util/chunkRecovery) grants a reload, suppress the throw — the page is
  // being replaced. Otherwise DO NOT preventDefault: preventing makes Vite
  // resolve the import with `undefined` (which used to surface as a
  // confusing TypeError); letting it reject sends it through loadChunk and
  // ends at a visible error screen instead.
  window.addEventListener("vite:preloadError", (event) => {
    if (attemptRecoveryReload()) event.preventDefault?.();
  });
}

// A store's own domain runs the storefront, which talks to the API over REST
// and issues no GraphQL — so it never downloads Apollo. The office does, from
// a chunk of its own; the request is started here, next to the entry chunk's
// own work, so it is not a round trip added in series.
const apollo = isMaskedHost() ? null : import('./apolloClient.js');

const renderApp = async () => {
  const root = ReactDOM.createRoot(document.getElementById('root'));
  if (apollo) {
    const { ApolloProvider, client } = await apollo;
    root.render(
      <AppErrorBoundary>
        <ApolloProvider client={client}>
          <App />
        </ApolloProvider>
      </AppErrorBoundary>
    );
  } else {
    root.render(
      <AppErrorBoundary>
        <App />
      </AppErrorBoundary>
    );
  }
  // Move this tab onto newer deployments at the first safe moment (see
  // util/versionWatchdog).
  startVersionWatchdog();
};

// The API painted the page's hero from the HTML (seoHeroOverlay /
// seoHomeHeroOverlay: headline + product photo). Let that photo paint before
// React takes the main thread and replaces it: on a fast connection the bundle
// could arrive first, the overlay was removed before its photo was on screen,
// and the largest paint became React's copy — which Lighthouse then charged
// with all the JavaScript run before it (PageSpeed: 1.2 s "render delay").
// Capped at 1.5 s so a slow photo never holds the app back for long.
const ssrHeroPainted = () => {
  const img = document.querySelector('#arego-ssr-hero img');
  if (!img || img.complete) return Promise.resolve();
  return new Promise((resolve) => {
    const cap = setTimeout(resolve, 1500);
    const done = () => { clearTimeout(cap); requestAnimationFrame(() => setTimeout(resolve, 0)); };
    img.addEventListener('load', done, { once: true });
    img.addEventListener('error', done, { once: true });
  });
};

// The cross-origin session handshake (adopting a handed-over session, asking
// office whether this browser is signed in) already ran in index.html, before
// this bundle was parsed. All that's left is resolving which store this domain
// maps to, which must happen before first render because maskedActive() gates
// the storefront slug and every internal link helper.
if (isMaskedHost()) {
  // Start this store's template chunk FIRST, before anything is awaited: it is
  // named in the head, so the download can run beside the storefront chunk
  // rather than two hops behind it. Without the head start the browser only
  // learns which template to fetch after the storefront chunk has run AND the
  // store API has answered — the split that made the bundle smaller would
  // have made the page later.
  resolveMaskedDomain();
  prefetchTemplate(maskedTemplate(), window.location.pathname);
  Promise.allSettled([resolveMaskedDomain(), ssrHeroPainted()]).finally(renderApp);
} else {
  // Anyone signed in before the sign-in chain shipped has no session planted on
  // the stores. The first office visit plants it, once per session, and comes
  // straight back here; a visitor with no token or an already-planted one
  // never notices this line.
  let planting = false;
  try {
    const q = new URLSearchParams(window.location.search);
    const handshake = q.get("aregoSsoCheck") === "1" || q.get("aregoSsoAdopt") === "1";
    if (window.location.origin === OFFICE_ORIGIN && !handshake) {
      planting = provisionStores(localStorage.getItem("token"), window.location.href);
    }
  } catch { planting = false; }
  if (!planting) renderApp();
}
